/**
 * @module engine3d/solid/build
 * @summary Evaluates a part's feature stack into the same flat `Scene` the
 *   plant view renders.
 * @description
 *   The whole point of targeting `Scene` rather than a new format: the part
 *   modeller gets the existing rasteriser's shading, painter's sort, soft
 *   shadows, labels and — because picking works off the projected triangles of
 *   the last frame — feature selection, without any of it being written twice.
 *   One `SceneObject` per feature is what makes a click in the viewport select a
 *   row in the feature tree.
 *
 *   Rebuild cost is a few milliseconds for a part of a few dozen features, and a
 *   rebuild happens when a PARAMETER changes, never when the camera moves. So
 *   dragging a dimension slider re-evaluates the stack on every pointer event
 *   and still holds sixty frames a second.
 *
 *   // INVARIANT: meshes are built fresh and then mutated in place by the
 *   // transform; nothing here is cached, so nothing can be aliased.
 */
import { MATERIALS, type MatKey, type Material } from '../materials';
import { deg, v3, type Vec2, type Vec3 } from '../math3';
import {
  box,
  cylinder,
  emptyMesh,
  extrude,
  frustum,
  merge,
  ngon,
  filletPath,
  revolve,
  rotateX,
  rotateY,
  rotateZ,
  sphere,
  torusArc,
  translate,
  tube,
  type Mesh,
} from '../mesh';
import { MAT_KEYS, type Scene, type SceneObject } from '../scene';
import { helix, rectLoft, vesselProfile, type VesselEnd } from './prims';
import {
  extrudeAlong,
  planePoint,
  sectionPoly,
  shellTube,
  squareToRound,
  troughU,
  type ExtrudeAxis,
  type SectionKind,
} from './profile';
import type { Sketch } from './part';
import { instanceCount, type Feature, type Part } from './part';

const MAT_INDEX: Record<string, number> = {};
MAT_KEYS.forEach((k, i) => (MAT_INDEX[k] = i));

/** Fall back to a visible material rather than index 0 if a key goes stale. */
const matIndexOf = (k: MatKey): number => MAT_INDEX[k] ?? MAT_INDEX.machine ?? 0;

// ─── One feature's geometry, in millimetres, before placement ────────────

const seg = (f: Feature, d = 28): number => Math.max(3, Math.round(f.p.seg ?? d));

/**
 * Build a feature's primitive at the origin, in millimetres.
 * Local origin is centred in X/Z and sits on y = 0, matching `mesh.ts` — which
 * is what lets a hopper's outlet height be read straight off its Y placement.
 */
export function featureMesh(f: Feature): Mesh {
  const p = f.p;
  switch (f.kind) {
    case 'box':
      return box(p.w, p.h, p.d);

    case 'cyl':
      return cylinder(p.r, p.h, { seg: seg(f) });

    case 'cone':
      return frustum(p.r0, p.r1, p.h, { seg: seg(f) });

    case 'loft':
      return rectLoft(p.w0, p.d0, p.w1, p.d1, p.h);

    case 'prism': {
      const n = Math.max(3, Math.round(p.n));
      return extrude(ngon(p.r, n, Math.PI / n) as Vec2[], p.h);
    }

    case 'sphere':
      // Raised onto the floor like every other primitive, so placing it is the
      // same operation as placing a box.
      return translate(sphere(p.r, seg(f), Math.max(6, seg(f) >> 1)), 0, p.r, 0);

    case 'pipe': {
      const body = cylinder(p.r, p.len, { seg: seg(f, 24) });
      if (p.flange < 0.5) return body;
      // Flanges are how a spool piece reads as a made part rather than a stick.
      const fr = p.r * 1.55 + 12;
      const ft = Math.max(8, p.r * 0.16);
      return merge(
        body,
        cylinder(fr, ft, { seg: seg(f, 24) }),
        translate(cylinder(fr, ft, { seg: seg(f, 24) }), 0, p.len - ft, 0),
      );
    }

    case 'elbow': {
      // torusArc sweeps about +Y in the XZ plane; stood up on its start face so
      // the elbow leaves the origin along +Y like a pipe does, and an elbow
      // dropped onto the end of a pipe therefore lines up without hunting.
      const m = torusArc(p.R, p.r, 0, deg(p.sweep), Math.max(4, Math.round(p.sweep / 6)), seg(f, 20));
      rotateX(m, -Math.PI / 2);
      return translate(m, -p.R, 0, 0);
    }

    case 'ring':
      return torusArc(p.R, p.r, 0, Math.PI * 2, Math.max(12, seg(f, 32)), 14);

    case 'helix':
      return helix(p.rIn, p.rOut, p.pitch, p.turns, p.t, 26);

    case 'vessel': {
      // One profile, both ends chosen independently — a silo is a cone under a
      // shell under a dished roof, so the two ends must not be forced to match.
      const end = (v: number): VesselEnd => (v === 1 ? 1 : v === 2 ? 2 : 0);
      const prof = vesselProfile(p.r, p.h, end(p.endBot), end(p.endTop), p.coneH);
      return revolve(prof, Math.max(8, seg(f, 32)), true);
    }

    case 'sqround':
      return squareToRound(p.w, p.d, p.r, p.h, p.flip > 0.5, seg(f, 32));

    case 'shell':
      return shellTube(p.r, p.wall, p.h, seg(f, 32));

    case 'trough':
      return troughU(p.r, p.sideH, p.wall, p.len);

    case 'section': {
      const shape = (Math.max(0, Math.min(3, Math.round(p.shape))) || 0) as SectionKind;
      return extrudeAlong(sectionPoly(shape, p.dep, p.wid, p.t), p.len, 'y');
    }

    case 'sketch':
      return sketchMesh(f);

    default:
      return box(100, 100, 100);
  }
}

/** The extrusion axis a sketch plane implies. */
const AXIS_OF: Record<Sketch['plane'], ExtrudeAxis> = { xz: 'y', xy: 'z', zy: 'x' };

/**
 * A hand-drawn profile made solid.
 *
 * Three operations, each already covered by the mesh kernel — which is why a
 * sketch tool costs a builder rather than a rewrite:
 *
 *   extrude  the profile, triangulated so a non-convex outline caps correctly;
 *   revolve  the profile about the Y axis, for anything turned or rolled;
 *   sweep    a round section along the profile taken as a PATH, for ducting and
 *            spouting that has to follow a particular route.
 *
 * Returns an empty mesh rather than throwing when there is not enough drawn
 * yet — a sketch is under construction most of the time it exists.
 */
function sketchMesh(f: Feature): Mesh {
  const sk = f.sketch;
  const p = f.p;
  if (!sk || sk.pts.length < 2) return emptyMesh();
  const pts = sk.pts;

  if (sk.op === 'sweep') {
    const axis = AXIS_OF[sk.plane];
    const path = pts.map((q) => planePoint(q, axis, 0));
    const line = p.bend > 0.5 ? filletPath(path, p.bend, 5) : path;
    return tube(line, Math.max(1, p.r), Math.max(6, Math.min(32, seg(f, 16))), true);
  }

  if (pts.length < 3) return emptyMesh();

  if (sk.op === 'revolve') {
    // Revolving is about the Y axis, so the profile's horizontal ordinate IS a
    // radius and cannot be negative: a point dragged across the axis would
    // otherwise turn the surface inside out. Mirroring it is what the user
    // meant, and it keeps the solid valid whichever side they drew on.
    const prof = pts.map((q) => ({ x: Math.abs(q.x), y: q.y }));
    return revolve(prof, Math.max(8, seg(f, 32)), true);
  }

  return extrudeAlong(pts, Math.max(0.5, p.h), AXIS_OF[sk.plane]);
}

/** Every instance offset a feature's pattern produces, itself first. */
export function patternOffsets(f: Feature): Vec3[] {
  const q = f.pattern;
  if (!q) return [v3(0, 0, 0)];
  const out: Vec3[] = [];
  const n = Math.max(1, Math.round(q.count));

  if (q.kind === 'circular') {
    const sweep = deg(q.sweep ?? 360);
    // A full ring closes on itself, so the last instance must not land on the
    // first: divide by the count, not by count - 1.
    const full = Math.abs((q.sweep ?? 360) - 360) < 0.5;
    for (let i = 0; i < n; i++) {
      const a = sweep * (full ? i / n : n > 1 ? i / (n - 1) : 0);
      out.push(v3(Math.cos(a) * q.spacing, 0, Math.sin(a) * q.spacing));
    }
    return out;
  }

  const n2 = Math.max(1, Math.round(q.count2 ?? 1));
  const step = (axis: 'x' | 'y' | 'z', k: number): Vec3 =>
    axis === 'x' ? v3(k, 0, 0) : axis === 'y' ? v3(0, k, 0) : v3(0, 0, k);
  for (let i = 0; i < n; i++)
    for (let j = 0; j < n2; j++) {
      const a = step(q.axis, q.spacing * i);
      const b = step(q.axis2 ?? 'z', (q.spacing2 ?? 0) * j);
      out.push(v3(a.x + b.x, a.y + b.y, a.z + b.z));
    }
  return out;
}

/**
 * A feature's mesh, transformed into part space (still millimetres).
 * Rotation order is X → Y → Z, which is what the placement panel's three boxes
 * read as top to bottom.
 */
export function placedMesh(f: Feature): Mesh {
  const m = featureMesh(f);
  if (f.rx) rotateX(m, deg(f.rx));
  if (f.ry) rotateY(m, deg(f.ry));
  if (f.rz) rotateZ(m, deg(f.rz));
  return translate(m, f.x, f.y, f.z);
}

// ─── Triangle accumulator ────────────────────────────────────────────────

/** Growable triangle buffer, doubling on demand and trimmed once at the end. */
class TriBuf {
  pos = new Float32Array(9 * 2048);
  nrm = new Float32Array(9 * 2048);
  mat = new Uint8Array(2048);
  obj = new Uint16Array(2048);
  n = 0;

  private grow(need: number): void {
    if (this.n + need <= this.mat.length) return;
    let cap = this.mat.length;
    while (cap < this.n + need) cap *= 2;
    const pos = new Float32Array(cap * 9);
    pos.set(this.pos);
    this.pos = pos;
    const nrm = new Float32Array(cap * 9);
    nrm.set(this.nrm);
    this.nrm = nrm;
    const mat = new Uint8Array(cap);
    mat.set(this.mat);
    this.mat = mat;
    const obj = new Uint16Array(cap);
    obj.set(this.obj);
    this.obj = obj;
  }

  /**
   * Append a mesh, scaling millimetres to world units and offsetting by a
   * pattern instance on the way in. Degenerate triangles are dropped here
   * rather than costing a sort slot and a canvas fill on every later frame.
   */
  add(m: Mesh, matIdx: number, objIdx: number, k: number, off: Vec3): void {
    const tris = m.idx.length / 3;
    this.grow(tris);
    for (let t = 0; t < tris; t++) {
      const base = this.n * 9;
      let degenerate = true;
      let px = 0;
      let py = 0;
      let pz = 0;
      for (let c = 0; c < 3; c++) {
        const vi = m.idx[t * 3 + c] * 3;
        const wx = (m.pos[vi] + off.x) * k;
        const wy = (m.pos[vi + 1] + off.y) * k;
        const wz = (m.pos[vi + 2] + off.z) * k;
        this.pos[base + c * 3] = wx;
        this.pos[base + c * 3 + 1] = wy;
        this.pos[base + c * 3 + 2] = wz;
        // A uniform scale leaves normals unchanged.
        this.nrm[base + c * 3] = m.nrm[vi];
        this.nrm[base + c * 3 + 1] = m.nrm[vi + 1];
        this.nrm[base + c * 3 + 2] = m.nrm[vi + 2];
        if (c === 0) {
          px = wx;
          py = wy;
          pz = wz;
        } else if (
          degenerate &&
          (Math.abs(wx - px) > 1e-4 || Math.abs(wy - py) > 1e-4 || Math.abs(wz - pz) > 1e-4)
        ) {
          degenerate = false;
        }
      }
      if (degenerate) continue;
      this.mat[this.n] = matIdx;
      this.obj[this.n] = objIdx;
      this.n++;
    }
  }

  boundsSince(from: number): { min: Vec3; max: Vec3; ok: boolean } {
    let ax = Infinity;
    let ay = Infinity;
    let az = Infinity;
    let bx = -Infinity;
    let by = -Infinity;
    let bz = -Infinity;
    for (let t = from; t < this.n; t++)
      for (let c = 0; c < 3; c++) {
        const i = t * 9 + c * 3;
        const x = this.pos[i];
        const y = this.pos[i + 1];
        const z = this.pos[i + 2];
        if (x < ax) ax = x;
        if (x > bx) bx = x;
        if (y < ay) ay = y;
        if (y > by) by = y;
        if (z < az) az = z;
        if (z > bz) bz = z;
      }
    return { min: v3(ax, ay, az), max: v3(bx, by, bz), ok: Number.isFinite(ax) };
  }
}

/** A box's footprint, used as the ground-shadow outline for every feature. */
function footprint(min: Vec3, max: Vec3): Vec2[] {
  return [
    { x: min.x, y: min.z },
    { x: max.x, y: min.z },
    { x: max.x, y: max.z },
    { x: min.x, y: max.z },
  ];
}

/** Statistics the modeller's read-out shows. */
export interface PartStats {
  features: number;
  instances: number;
  triangles: number;
  /** Overall size in millimetres. */
  size: Vec3;
}

export interface BuiltPart {
  scene: Scene;
  stats: PartStats;
}

/**
 * Evaluate the stack. Suppressed features are skipped but keep their tree row,
 * exactly as suppressing a feature in CAD does.
 */
export function buildPart(part: Part): BuiltPart {
  const k = 1 / Math.max(0.5, part.mmPerUnit);
  const buf = new TriBuf();
  const objects: SceneObject[] = [];
  let x0 = Infinity;
  let y0 = Infinity;
  let z0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  let z1 = -Infinity;
  let instances = 0;

  for (const f of part.features) {
    if (f.suppressed) continue;
    const objIdx = objects.length;
    const start = buf.n;
    let mesh: Mesh;
    try {
      mesh = placedMesh(f);
    } catch {
      continue; // One bad feature must not sink the whole part.
    }
    const mi = matIndexOf(f.mat);
    const offs = patternOffsets(f);
    instances += offs.length;
    for (const off of offs) buf.add(mesh, mi, objIdx, k, off);

    const b = buf.boundsSince(start);
    if (!b.ok) continue;
    if (b.min.x < x0) x0 = b.min.x;
    if (b.min.y < y0) y0 = b.min.y;
    if (b.min.z < z0) z0 = b.min.z;
    if (b.max.x > x1) x1 = b.max.x;
    if (b.max.y > y1) y1 = b.max.y;
    if (b.max.z > z1) z1 = b.max.z;

    const opaque = ((MATERIALS[f.mat] as Material | undefined)?.alpha ?? 1) > 0.9;
    objects.push({
      id: f.id,
      kind: 'shape',
      type: f.kind,
      label: f.name,
      tag: '',
      labelAt: v3((b.min.x + b.max.x) / 2, b.max.y, (b.min.z + b.max.z) / 2),
      min: b.min,
      max: b.max,
      // Glass and screens casting a hard shadow looks like a bug, so they do not.
      shadow: opaque ? footprint(b.min, b.max) : [],
      shadowY0: Math.max(0, b.min.y),
      shadowY1: b.max.y,
      tint: null,
      tintAmt: 0,
    });
  }

  // An empty part still needs bounds for the CAMERA to frame something, but its
  // reported size must stay zero — a placeholder box is not a 400 mm part.
  const built = Number.isFinite(x0);
  if (!built) {
    x0 = -8;
    y0 = 0;
    z0 = -8;
    x1 = 8;
    y1 = 8;
    z1 = 8;
  }

  return {
    scene: {
      pos: buf.pos.subarray(0, buf.n * 9),
      nrm: buf.nrm.subarray(0, buf.n * 9),
      mat: buf.mat.subarray(0, buf.n),
      obj: buf.obj.subarray(0, buf.n),
      // A part has no pipework and nothing flowing through it, so nothing here
      // carries a distance along a run.
      flowS: new Float32Array(buf.n).fill(-1),
      count: buf.n,
      objects,
      levels: [],
      dims: [],
      userDims: [],
      // The floor is drawn at y = 0, so the bounds start there even when the
      // part itself is raised on legs.
      min: v3(x0, Math.min(0, y0), z0),
      max: v3(x1, y1, z1),
    },
    stats: {
      features: part.features.filter((f) => !f.suppressed).length,
      instances,
      triangles: buf.n,
      // Reported in millimetres, which is what the modeller is dimensioned in.
      size: built ? v3((x1 - x0) / k, (y1 - y0) / k, (z1 - z0) / k) : v3(),
    },
  };
}

/** Instance count across the whole part, for the read-out before a build. */
export const partInstances = (part: Part): number =>
  part.features.reduce((n, f) => n + (f.suppressed ? 0 : instanceCount(f)), 0);
